<template>
  <agent-default>
    <div class="tw-w-full">
      <div class="sale-body">

        <!-- Header -->
        <header class="sale-header">
          <div class="tw-flex tw-items-center tw-gap-3">
            <button class="back-btn" @click="goToDashboard('finalization_back')">
              <v-icon size="18" color="#1E4276">mdi-arrow-left</v-icon>
            </button>
            <div>
              <p class="sale-breadcrumb">Underwriting / Progress Tracker</p>
              <h2 class="sale-title">Policy Issuance Progress</h2>
            </div>
          </div>
          <div v-if="!loading" class="status-chip" :class="statusClass">
            <v-icon x-small :color="statusColor" class="tw-mr-1">mdi-circle</v-icon>
            <span>{{ currentStatus }}</span>
          </div>
        </header>

        <!-- Loading State -->
        <div v-if="loading" class="tw-w-full tw-flex tw-justify-center tw-py-20">
          <v-progress-circular :size="64" color="#00AEEF" indeterminate />
          <p class="tw-ml-4 tw-text-[#1E4276] tw-font-bold tw-flex tw-items-center">Fetching policy status...</p>
        </div>

        <div v-else>
          <!-- Progress Stepper -->
        <div class="progress-container tw-mb-8">
          <div
            v-for="(step, i) in steps"
            :key="i"
            class="progress-step"
            :class="{ 'step-complete': step.done, 'step-active': step.active }"
          >
            <div class="step-line" v-if="i > 0"></div>
            <div class="step-node">
              <v-icon v-if="step.done" size="14" color="white">mdi-check</v-icon>
              <div v-else-if="step.active" class="active-pulse"></div>
              <span v-else>{{ i + 1 }}</span>
            </div>
            <div class="step-info">
              <p class="step-label">{{ step.label }}</p>
              <p class="step-time" v-if="step.done">{{ step.time }}</p>
              <p class="step-hint" v-else-if="step.active">{{ step.hint }}</p>
            </div>
          </div>
        </div>

        <!-- Vehicle Summary Card -->
        <div class="summary-card tw-mb-6">
          <div class="summary-head">
            <v-icon color="#00AEEF" class="tw-mr-2">mdi-car-info</v-icon>
            <span>Vehicle Details</span>
          </div>
          <div class="summary-grid">
            <div class="info-item">
              <label>Registration</label>
              <p class="tw-font-bold">{{ vehicle.regNo }}</p>
            </div>
            <div class="info-item">
              <label>Vehicle</label>
              <p>{{ vehicle.make }} {{ vehicle.model }} ({{ vehicle.yom }})</p>
            </div>
            <div class="info-item">
              <label>Chassis</label>
              <p class="tw-text-xs tw-font-mono">{{ vehicle.chassis }}</p>
            </div>
            <div class="info-item">
              <label>Quote ID</label>
              <p>#{{ quoteId }}</p>
            </div>
          </div>
        </div>

        <!-- Guidance Banner -->
        <div v-if="guidance" class="guidance-banner tw-mb-8">
          <v-icon color="#1E4276" class="tw-mr-3">mdi-information-outline</v-icon>
          <div class="guidance-body">
            <h4 class="tw-text-sm tw-font-bold tw-text-[#1E4276] tw-mb-1">{{ guidance.title }}</h4>
            <p class="tw-text-xs tw-text-[#64748B] tw-m-0" v-html="guidance.message"></p>
          </div>
        </div>

        <!-- Actions -->
        <div class="tw-flex tw-gap-4">
          <button class="btn-outline tw-flex-1" @click="goToDashboard('finalization_dashboard_button')">
            <v-icon small class="tw-mr-2">mdi-view-dashboard</v-icon> Dashboard
          </button>

          <button
            v-if="quoteData && quoteData.step === 'QUOTE_CREATED'"
            class="btn-primary tw-flex-1"
            @click="resumeQuote"
          >
            <v-icon small color="white" class="tw-mr-2">mdi-play-circle-outline</v-icon> Resume Application
          </button>

          <button
            v-else
            class="btn-primary tw-flex-1"
            @click="goToUnderwriting"
          >
            <v-icon small color="white" class="tw-mr-2">mdi-plus</v-icon> New Motor Quote
          </button>
        </div>

        </div>

      </div>
    </div>
  </agent-default>
</template>

<script>
import AgentDefault from '@/components/layout/AgentDefault';
import axios from 'axios';
import { trackAgentInteraction } from '@/utils/agentAnalytics';

export default {
  name: 'AgentMotorFinalization',
  components: { AgentDefault },
  props: ['quoteId'],
  data() {
    return {
      loading: true,
      quoteData: null,
      vehicle: {
        regNo: '...',
        make: '...',
        model: '...',
        yom: '...',
        chassis: '...',
      },
      steps: [
        {
          label: 'Payment Received', done: false, active: false, time: '',
        },
        {
          label: 'UW Review', done: false, active: false, hint: '',
        },
        { label: 'Inspection', done: false, active: false },
        { label: 'Issuance', done: false, active: false },
      ],
    };
  },
  computed: {
    currentStatus() {
      if (this.loading) return 'Loading...';
      if (!this.quoteData) return 'Not Found';
      const issued = 'Policy Issued';
      const uw = 'Underwriting Review';
      const statusMap = {
        QUOTE_CREATED: 'Awaiting Payment',
        POLICY_PAYMENT: 'Payment Processing',
        UW_REVIEW: uw,
        QUOTATION_REVIEW: uw,
        INSPECTION_PENDING: 'Inspection Pending',
        CERTIFICATE_READY: issued,
        POLICY_ISSUED: issued,
        COMPLETE: issued,
      };
      return statusMap[this.quoteData.step] || this.quoteData.step;
    },
    statusClass() {
      const s = this.quoteData?.step;
      const issues = ['POLICY_ISSUED', 'CERTIFICATE_READY', 'COMPLETE'];
      if (!s) return 'status-processing';
      if (issues.includes(s)) return 'status-complete';
      if (s === 'QUOTE_CREATED') return 'status-pending';
      return 'status-processing';
    },
    statusColor() {
      const s = this.quoteData?.step;
      const issues = ['POLICY_ISSUED', 'CERTIFICATE_READY', 'COMPLETE'];
      if (issues.includes(s)) return '#10B981';
      if (s === 'QUOTE_CREATED') return '#FFC107';
      return '#00AEEF';
    },
    guidance() {
      if (!this.quoteData) return null;
      const s = this.quoteData.step;
      const issuedTitle = 'Policy Issued';
      const issuedMsg = 'The policy is fully issued. You can now download the certificate and share it with the client.';
      const uwTitle = 'Underwriting Review';
      const uwMsg = 'The policy is undergoing underwriting review. You will be notified once the certificate is ready.';
      const steps = {
        QUOTE_CREATED: {
          title: 'Awaiting Payment',
          message: 'The quote has been generated. Please <strong>proceed to payment</strong> to initiate the underwriting process.',
        },
        POLICY_PAYMENT: {
          title: 'Payment Verification',
          message: 'We are currently verifying the payment. Once confirmed, the policy will move to underwriting review.',
        },
        UW_REVIEW: { title: uwTitle, message: uwMsg },
        QUOTATION_REVIEW: { title: uwTitle, message: uwMsg },
        INSPECTION_PENDING: {
          title: 'Inspection Required',
          message: 'The vehicle needs to be inspected. Please <strong>remind the client</strong> to visit an authorized inspection center.',
        },
        CERTIFICATE_READY: { title: issuedTitle, message: issuedMsg },
        POLICY_ISSUED: { title: issuedTitle, message: issuedMsg },
        COMPLETE: { title: issuedTitle, message: issuedMsg },
      };
      return steps[s] || {
        title: "What's Next?",
        message: 'The policy is being processed. We will notify you once there is an update on the status.',
      };
    },
  },
  async mounted() {
    await this.fetchQuoteDetails();
  },
  methods: {
    goToDashboard(source) {
      trackAgentInteraction(this, 'finalization_open_dashboard', { source });
      this.$router.push({ name: 'AgentDashboard' });
    },
    goToUnderwriting() {
      trackAgentInteraction(this, 'finalization_new_quote');
      this.$router.push({ name: 'AgentUnderwriting' });
    },
    async fetchQuoteDetails() {
      const qId = this.quoteId || this.$route.params.quoteId;
      if (!qId || qId === 'undefined') {
        this.loading = false;
        this.$notify.error('Invalid Quote ID provided');
        return;
      }
      this.loading = true;
      try {
        const token = this.$store.getters['auth/accessToken'];
        const res = await axios.get('/customer/quotation/agent/', {
          params: { quoteId: qId },
          headers: {
            auth: token,
            Authorization: `Bearer ${token}`,
          },
          _skipErrorRedirect: true,
        });
        const data = res.data?.data;
        if (data) {
          this.quoteData = data;
          const car = data.covertypes?.[0]?.car;
          if (car) {
            this.vehicle = {
              regNo: car.registrationNumber || car.regNo || 'N/A',
              make: car.make || 'N/A',
              model: car.model || 'N/A',
              yom: car.yearOfManufacture || car.yom || 'N/A',
              chassis: car.chassisNumber || car.chassis || 'N/A',
            };
          }
          this.mapSteps(data);
        } else {
          this.$notify.error('Quote not found');
        }
      } catch (e) {
        console.error('Failed to fetch quote', e);
        this.$notify.error('Could not load policy details');
      } finally {
        this.loading = false;
      }
    },
    mapSteps(quote) {
      const s = quote.step;

      // Reset all steps first
      this.steps = this.steps.map((step) => ({
        ...step,
        done: false,
        active: false,
        time: '',
        hint: '',
      }));

      if (s === 'QUOTE_CREATED') {
        // Nothing done yet — quote exists but no payment
        this.steps[0].active = true;
        this.steps[0].hint = 'Awaiting payment...';
        return;
      }

      // Payment done for all steps beyond QUOTE_CREATED
      this.steps[0].done = true;
      this.steps[0].time = 'Confirmed';

      if (s === 'POLICY_PAYMENT') {
        this.steps[0].active = true;
        this.steps[0].hint = 'Processing payment...';
        return;
      }

      if (s === 'UW_REVIEW' || s === 'QUOTATION_REVIEW') {
        this.steps[1].active = true;
        this.steps[1].hint = 'Verifying NTSA details...';
        return;
      }

      if (s === 'INSPECTION_PENDING') {
        this.steps[1].done = true;
        this.steps[2].active = true;
        this.steps[2].hint = 'Schedule inspection...';
        return;
      }

      if (['POLICY_ISSUED', 'CERTIFICATE_READY', 'COMPLETE'].includes(s)) {
        this.steps[1].done = true;
        this.steps[2].done = true;
        this.steps[3].done = true;
        this.steps[3].time = 'Issued';
      }
    },
    resumeQuote() {
      if (!this.quoteData) return;
      trackAgentInteraction(this, 'finalization_resume_quote', {
        quote_id: this.quoteId || this.$route.params.quoteId || 'unknown',
      });
      const s = this.quoteData.step;
      const qId = this.quoteId || this.$route.params.quoteId;

      if (s === 'QUOTE_CREATED') {
        this.$router.push({
          name: 'AgentSale',
          query: { quoteId: qId },
        });
      } else {
        // For all other steps, they are already "resumed" by seeing this status tracker
        this.$notify.info('Application is already in progress');
      }
    },
  },
};
</script>

<style scoped>
.sale-body { padding: 40px 20px; max-width: 800px; margin: 0 auto; }
.sale-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 32px; }
.back-btn { width: 36px; height: 36px; border-radius: 10px; border: 1.5px solid #E2E8F0; background: white; display: flex; align-items: center; justify-content: center; }
.sale-breadcrumb { font-size: 0.7rem; color: #94A3B8; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; margin: 0; }
.sale-title { font-size: 1.25rem; font-weight: 800; color: #1E4276; margin: 0; }

.status-chip { display: flex; align-items: center; padding: 6px 14px; border-radius: 20px; font-size: 0.8rem; font-weight: 700; }
.status-processing { background: rgba(0, 174, 239, 0.08); color: #00AEEF; border: 1px solid rgba(0, 174, 239, 0.2); }
.status-pending { background: rgba(255, 193, 7, 0.08); color: #FFC107; border: 1px solid rgba(255, 193, 7, 0.2); }
.status-complete { background: rgba(16, 185, 129, 0.08); color: #10B981; border: 1px solid rgba(16, 185, 129, 0.2); }

.progress-container { position: relative; display: flex; justify-content: space-between; padding: 20px 0; }
.progress-step { position: relative; flex: 1; display: flex; flex-direction: column; align-items: center; text-align: center; }
.step-node {
  width: 32px; height: 32px; border-radius: 50%; background: #F1F5F9;
  display: flex; align-items: center; justify-content: center;
  font-size: 0.8rem; font-weight: 800; color: #94A3B8; z-index: 2;
}
.step-line {
  position: absolute; top: 16px; left: -50%; width: 100%; height: 2px;
  background: #F1F5F9; z-index: 1;
}
.step-complete .step-node { background: #10B981; color: white; }
.step-complete .step-line { background: #10B981; }
.step-active .step-node { background: #00AEEF; color: white; box-shadow: 0 0 0 4px rgba(0, 174, 239, 0.15); }

.active-pulse { width: 10px; height: 10px; background: white; border-radius: 50%; animation: pulse 1.5s infinite; }
@keyframes pulse { 0% { opacity: 1; } 50% { opacity: 0.4; } 100% { opacity: 1; } }

.step-info { margin-top: 12px; }
.step-label { font-size: 0.85rem; font-weight: 700; color: #1E4276; margin: 0; }
.step-time { font-size: 0.7rem; color: #94A3B8; margin-top: 2px; }
.step-hint { font-size: 0.7rem; color: #00AEEF; font-weight: 600; margin-top: 2px; }

.summary-card { background: white; border-radius: 16px; border: 1px solid #E8EDF2; overflow: hidden; }
.summary-head { padding: 14px 20px; background: #F8FBFF; border-bottom: 1px solid #E8EDF2; font-weight: 700; color: #1E4276; display: flex; align-items: center; }
.summary-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; padding: 20px; }
.info-item label { font-size: 0.75rem; color: #94A3B8; font-weight: 600; display: block; margin-bottom: 4px; }
.info-item p { margin: 0; color: #1E4276; font-weight: 500; font-size: 0.9rem; }

.guidance-banner {
  display: flex; align-items: center; background: #EBF8FF;
  border: 1px solid #BAE6FD; border-radius: 12px; padding: 16px;
}

.btn-primary { height: 48px; background: #00AEEF; color: white; border-radius: 12px; font-weight: 700; box-shadow: 0 4px 12px rgba(0, 174, 239, 0.2); }
.btn-outline { height: 48px; border: 1.5px solid #E2E8F0; color: #1E4276; border-radius: 12px; font-weight: 700; }
</style>
